import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { PageLoader } from "@/components/common/Loading";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Trash2, RotateCcw } from "lucide-react";
import { toast } from "sonner";

export default function RecycleBin() {
  const { currentAccount } = useAuth();
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/recycle-bin"); setData(data); }
    catch (e) { toast.error(apiError(e)); }
  }, [currentAccount]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <PageLoader />;

  const restorePerson = async (id) => { try { await api.post(`/persons/${id}/restore`); toast.success("Restored"); load(); } catch (e) { toast.error(apiError(e)); } };
  const restoreOrg = async (id) => { try { await api.post(`/organizations/${id}/restore`); toast.success("Restored"); load(); } catch (e) { toast.error(apiError(e)); } };

  const empty = !data.persons.length && !data.organizations.length && !data.custom_fields.length;

  return (
    <div>
      <PageHeader title="Recycle Bin" description="Soft-deleted records. Permanent deletion is restricted." />
      {empty ? (
        <EmptyState icon={Trash2} title="Recycle bin is empty" description="Deleted records will appear here for restoration." />
      ) : (
        <div className="space-y-6">
          <Section title="People" items={data.persons} label={(p) => `${p.first_name} ${p.last_name} (${p.human_id})`} onRestore={restorePerson} testPrefix="rb-person" />
          <Section title="Organizations" items={data.organizations} label={(o) => `${o.name} (${o.org_code})`} onRestore={restoreOrg} testPrefix="rb-org" />
          {data.custom_fields.length > 0 && (
            <div>
              <h3 className="font-display font-semibold text-slate-900 text-sm mb-2">Custom Fields</h3>
              <div className="space-y-2">
                {data.custom_fields.map((f) => (
                  <div key={f.id} className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600">{f.label} <span className="text-xs text-slate-400">(restore not available in Phase I)</span></div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ title, items, label, onRestore, testPrefix }) {
  if (!items.length) return null;
  return (
    <div>
      <h3 className="font-display font-semibold text-slate-900 text-sm mb-2">{title}</h3>
      <div className="space-y-2">
        {items.map((it) => (
          <div key={it.id} className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-4 py-3">
            <div><p className="text-sm font-medium text-slate-700">{label(it)}</p>
              <p className="text-xs text-slate-400">Deleted {it.deleted_at ? new Date(it.deleted_at).toLocaleString() : ""}</p></div>
            <Button size="sm" variant="outline" onClick={() => onRestore(it.id)} data-testid={`${testPrefix}-restore-${it.id}`}>
              <RotateCcw className="w-4 h-4 mr-1.5" /> Restore
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
